import type { ComposerReasonCode, ComposerState } from '@/api/models';
import { cn } from '@/lib/cn';
import { reasonCopy, type BlockTone } from './copy/reasons';

/**
 * The closed composer.
 *
 * The server hands us `can_prompt` and a `reason_code` from the same predicate
 * that produces the 409 and re-checks the run at start, so the reason is never
 * guessed at here - only worded. Every one of the twenty-three codes has its
 * own string, because "you can't send this right now" is how a support ticket
 * arrives that nobody can answer.
 *
 * The server also sends a `message` for the same code. It is NOT rendered here:
 * it states the same fact this file already words, and showing both printed the
 * situation twice in two voices. The field stays on the wire for the 409 path
 * and for any consumer that is not this renderer.
 */

const TONE_STYLES: Record<BlockTone, { box: string; title: string; glyph: 'clock' | 'run' | 'stop' }> = {
  working: { box: 'bg-mint', title: 'text-graphite-900', glyph: 'run' },
  waiting: { box: 'bg-blush', title: 'text-signal', glyph: 'run' },
  paused: { box: 'bg-bone', title: 'text-graphite-900', glyph: 'clock' },
  fault: { box: 'bg-alarm-wash', title: 'text-alarm', glyph: 'stop' },
  finished: { box: 'bg-bone', title: 'text-graphite-900', glyph: 'clock' },
};

function Glyph({ shape }: { shape: 'clock' | 'run' | 'stop' }) {
  return (
    <span aria-hidden className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-bone-raised/70">
      <svg viewBox="0 0 12 12" className="h-[15px] w-[15px]" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="6" cy="6" r="4.6" />
        {shape === 'clock' ? <path d="M6 3.4V6l1.8 1.4" /> : null}
        {shape === 'run' ? <path d="M6 6V3.6" /> : null}
        {shape === 'stop' ? <path d="M4.2 7.8 7.8 4.2" /> : null}
      </svg>
    </span>
  );
}

export function ComposerBlocked({
  composer,
  className,
  supportEmail,
}: {
  composer: ComposerState | null | undefined;
  className?: string;
  supportEmail?: string;
}) {
  const code = (composer?.reason_code ?? 'not_assigned') as ComposerReasonCode;
  const copy = reasonCopy(code);
  const tone = TONE_STYLES[copy.tone];
  const retryAfter = composer?.retry_after_seconds ?? null;

  return (
    <div className={cn('flex items-center gap-3.5 rounded-lg px-[22px] py-4', tone.box, className)} role="status">
      <Glyph shape={tone.glyph} />
      <div className="min-w-0 flex-1">
        <p className={cn('text-[0.96875rem] font-semibold tracking-[-0.015em]', tone.title)}>{copy.title}</p>
        <p className="mt-0.5 text-[0.90625rem] text-graphite-500">{copy.body}</p>

        {retryAfter ? (
          <p className="mt-1.5 font-mono text-[0.71875rem] tabular-nums text-graphite-300">
            try again in about {Math.max(1, Math.ceil(retryAfter / 60))} min
          </p>
        ) : null}
      </div>

      {copy.contact ? (
        <a
          href={`mailto:${supportEmail ?? 'support@example.com'}`}
          className="inline-flex h-[34px] shrink-0 items-center rounded-full bg-bone-raised px-4 text-[0.8125rem] font-semibold text-graphite-900 shadow-[inset_0_0_0_1.5px_var(--rule-strong)] hover:bg-bone"
        >
          Get in touch
        </a>
      ) : null}
    </div>
  );
}
